var $ = require('jquery');
var Provider = require('./providers/Provider');

/**
 * Class that aggregates multiple search providers for the toolbar and provides
 * a central point for operating the search
 *
 * @depends Provider.js
 */
(function() {
    'use strict';

    var ProviderAggregator = Klazz.extend({
        /**
         * Defined providers
         *
         * A map of string label to provider object
         * @type {Object.<string, Provider>}
         */
        providers: null,
        /**
         * @type {Object.<string, Provider>}
         */
        loadedProviders: null,

        initialize: function() {
            this.providers = {};
            this.loadedProviders = {};
        },

        /**
         *
         * @param {String} name a label string serving as UID
         * @param {Provider} providerObj
         */
        addProvider: function(name, providerObj) {
            if ('string' !== typeof name) {
                throw new Error("name must be a string (" + name + ")");
            }

            if (!(providerObj instanceof Provider)) {
                throw new Error("Invalid provider object");
            }

            if (this.providers[name]) {
                throw new Error("Provider `" + name + "` is already added");
            }

            this.providers[name] = providerObj;
        },

        /**
         * Adds a list of providers
         * @param {Object.<string, Provider>} providers
         */
        addProviders: function(providers) {
            var name;

            if ('object' == typeof providers) {
                for (name in providers) {
                    if (providers.hasOwnProperty(name)) {
                        this.addProvider(name, providers[name]);
                    }
                }
            }
        },

        /**
         * @param {String} name
         * @returns {Provider}
         */
        getProvider: function(name) {
            if (!this.providers[name]) {
                throw new Error("Provider `" + name + "` not found");
            }

            return this.providers[name];
        },

        /**
         * Loads all defined providers asynchronously, returning a promise
         * @returns {Q.Promise}
         */
        loadProviders: function() {
            var loadPromises = [];

            $.each(this.providers, function(name, provider) {
                // Here we create a new promise for each provider,
                // starting from the existing load promise, which we
                // "spice" with some contextual information which allows
                // use to determine what provider loaded. Otherwise we'll only
                // know that the load occurred.
                loadPromises.push(
                    provider
                        .load()
                        .then(function() {
                            return {
                                name: name,
                                provider: provider
                            }
                        })
                );
            });

            // We load the providers in parallel and fulfill the promise when all
            // providers are finished (either with success or failure)
            return Q.allSettled(loadPromises)
                .then($.proxy(function(promises) {
                    var i, promise;

                    for (i = 0; i < promises.length; ++i) {
                        promise = promises[i];
                        if (promise.state == 'fulfilled') {
                            this.loadedProviders[promise.value.name] = promise.value.provider;
                        }
                    }

                    return this.loadedProviders;
                }, this));
        },

        /**
         * Checks if there are any defined providers
         * @returns {boolean}
         */
        hasProviders: function() {
            return !$.isEmptyObject(this.providers);
        },

        /**
         * Returns the list of providers that were successfully loaded
         * @returns {Object.<string, Provider>}
         */
        getLoadedProviders: function() {
            return this.loadedProviders;
        },

        /**
         * Number of providers that were successfully loaded
         * @returns {number}
         */
        getLoadedProviderCount: function() {
            var count = 0, name;
            for (name in this.loadedProviders) {
                if (this.loadedProviders.hasOwnProperty(name)) {
                    count++;
                }
            }

            return count;
        },

        /**
         * Checks if a provider was loaded
         * @param {String} name
         * @returns {boolean}
         */
        hasLoadedProvider: function(name) {
            return !!this.loadedProviders[name];
        },

        /**
         * Searches in all available providers.
         *
         * Returns an object with results from each provider, mapped by provider name
         * For a provider, an entry is created in the results only if some results were found
         *
         * @param {String} q the search query
         * @return {Object.<string, Object[]>}
         */
        search: function(q) {
            var searchResults = {};

            $.each(this.getLoadedProviders(), function(name, provider) {
                var res = provider.search(q);
                if (res && res.length) {
                    searchResults[name] = res;
                }
            });

            return searchResults;
        }
    });

    module.exports = ProviderAggregator;
}());



/*****************
 ** WEBPACK FOOTER
 ** ./source/cdn/document_root/s/diy/search/ProviderAggregator.js
 ** module id = 848
 ** module chunks = 0
 **/